import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { EventName } from '@verifyloop/core';
import { useLiveEvent } from './events';

export interface Resource<T> {
  data: T | null;
  error: Error | null;
  /** True only for the very first load; refetches keep the old rows visible. */
  loading: boolean;
  /** True while any refetch is in flight. */
  refreshing: boolean;
  reload: () => void;
  setData: (next: T | ((current: T | null) => T | null)) => void;
}

export interface UseResourceOptions {
  /**
   * Refetch when one of these live events arrives. This is the whole live
   * update story: the SSE payload only names the entity that changed, so the
   * honest response is to re-read the list the row lives in.
   */
  live?: readonly EventName[];
  /** Skip loading entirely (e.g. a dependent resource whose id is not known yet). */
  enabled?: boolean;
}

/**
 * A read-with-refresh, not a state manager: fetch once per `deps`, expose the
 * result, and let the caller decide what to do about failures.
 */
export function useResource<T>(
  loader: () => Promise<T>,
  deps: readonly unknown[],
  options: UseResourceOptions = {},
): Resource<T> {
  const { live, enabled = true } = options;

  const [data, setDataState] = useState<T | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const [loading, setLoading] = useState<boolean>(enabled);
  const [refreshing, setRefreshing] = useState(false);
  const [tick, setTick] = useState(0);

  const loaderRef = useRef(loader);
  loaderRef.current = loader;

  const requestId = useRef(0);
  const mounted = useRef(true);
  const everLoaded = useRef(false);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  useEffect(() => {
    if (!enabled) {
      setLoading(false);
      return;
    }
    const id = requestId.current + 1;
    requestId.current = id;
    let cancelled = false;
    if (everLoaded.current) setRefreshing(true);
    else setLoading(true);

    loaderRef
      .current()
      .then((value) => {
        if (cancelled || !mounted.current || requestId.current !== id) return;
        everLoaded.current = true;
        setDataState(value);
        setError(null);
      })
      .catch((cause: unknown) => {
        if (cancelled || !mounted.current || requestId.current !== id) return;
        setError(cause instanceof Error ? cause : new Error(String(cause)));
      })
      .finally(() => {
        if (cancelled || !mounted.current || requestId.current !== id) return;
        setLoading(false);
        setRefreshing(false);
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, tick, ...deps]);

  const reload = useCallback(() => setTick((value) => value + 1), []);

  useLiveEvent(live ?? [], () => {
    setTick((value) => value + 1);
  });

  const setData = useCallback((next: T | ((current: T | null) => T | null)) => {
    setDataState((current) =>
      typeof next === 'function' ? (next as (c: T | null) => T | null)(current) : next,
    );
  }, []);

  return useMemo(
    () => ({ data, error, loading, refreshing, reload, setData }),
    [data, error, loading, refreshing, reload, setData],
  );
}

/**
 * Fire-and-forget action with pending/error state, so a button can disable
 * itself and a failure can land in a toast without the page owning a try/catch.
 */
export function useAction<Args extends unknown[], Result>(
  action: (...args: Args) => Promise<Result>,
  options: { onSuccess?: (result: Result) => void; onError?: (error: Error) => void } = {},
): { run: (...args: Args) => Promise<Result | undefined>; pending: boolean; error: Error | null } {
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const actionRef = useRef(action);
  actionRef.current = action;
  const handlers = useRef(options);
  handlers.current = options;

  const run = useCallback(async (...args: Args): Promise<Result | undefined> => {
    setPending(true);
    setError(null);
    try {
      const result = await actionRef.current(...args);
      handlers.current.onSuccess?.(result);
      return result;
    } catch (cause) {
      const err = cause instanceof Error ? cause : new Error(String(cause));
      setError(err);
      handlers.current.onError?.(err);
      return undefined;
    } finally {
      setPending(false);
    }
  }, []);

  return { run, pending, error };
}